import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { numberFormat } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface LabOpreativeReport {
    title: string
    statusFilter: string
    data: { rowDetail: any[]; rowDf: any[]; rowLab: any[] }
    snType: boolean
    t: any
}

export async function exportLabOpreativeToExcelMonth(report: LabOpreativeReport) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(report.title, { pageSetup: { orientation: 'landscape' } })
    ws.properties.defaultRowHeight = 20

    // กำหนดคอลัมน์ตามหัวตารางเดือน
    ws.columns = [
        { width: 6 },   // ลำดับ
        { width: 15 },  // วันที่
        { width: 15 },  // เวลา
        { width: 10 },  // นัด
        { width: 10 },  // ผิดนัด
        { width: 10 },  // Walk-in
        { width: 10 },  // รวม
        { width: 15 },  // ราคาจ่ายจริง
        { width: 15 },  // DF4
        { width: 15 },  // CreditNet
        { width: 15 },  // TreatmentNet
        { width: 15 },  // DF5
        { width: 15 },  // ค่าประกัน
        { width: 15 },  // LAB Cost
        { width: 15 },  // DF6
    ]

    // Title row
    ws.mergeCells(1, 1, 1, ws.columns.length)
    ws.getCell(1, 1).value = report.title
    ws.getCell(1, 1).font = { size: 16, bold: true }
    ws.getCell(1, 1).alignment = { horizontal: 'center', vertical: 'middle' }

    // Status filter row
    ws.mergeCells(2, 1, 2, ws.columns.length)
    ws.getCell(2, 1).value = report.statusFilter
    ws.getCell(2, 1).font = { size: 12 }
    ws.getCell(2, 1).alignment = { horizontal: 'left', vertical: 'middle' }

    // Header row (แถวที่ 4)
    const headers = [
        report.t('TABLE.NO'),
        report.t('REPORT.TABLE.DF.DATE'),
        report.t('REPORT.TABLE.DF.TIME'),
        report.t('REPORT.TABLE.DF.APPOINTMENT'),
        report.t('REPORT.TABLE.DF.WRONG_APPOINTMENT'),
        report.t('REPORT.TABLE.DF.WALK_IN'),
        report.t('REPORT.TABLE.DF.SUM_APPOINTMENT'),
        report.t('REPORT.TABLE.DF.PRICE'),
        report.t('REPORT.TABLE.DF.DF4'),
        report.t('REPORT.TABLE.DF.CREDIT_NET'),
        report.t('REPORT.TABLE.DF.TREATMENT_NET'),
        report.t('REPORT.TABLE.DF.DF5'),
        report.t('REPORT.TABLE.DF.HAND'),
        report.t('REPORT.TABLE.DF.LAB_COST'),
        report.t('REPORT.TABLE.DF.DF6'),
    ]
    const headerRow = ws.getRow(4)
    headerRow.values = headers
    headerRow.font = { bold: true }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
    headerRow.height = 24
    headerRow.eachCell(cell => {
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
    })

    let currentRow = 5

    // แยกกลุ่มหมอตาม doctorId
    const grouped = _.chain(report.data.rowDf)
        .groupBy('doctorId')
        .map((rows: any[], doctorId: string) => ({
            doctorFullname: _.get(_.find(rows, 'doctorFullname'), 'doctorFullname', '-'),
            rows
        }))
        .value()

    const calculateDf6 = (o: any) => (o.dfNet > o.handInsurance ? o.dfNet - o.labCost : o.handInsurance - o.labCost)


    if (grouped.length === 0) {
        const noDataRow = ws.addRow(['ไม่พบข้อมูล'])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        noDataRow.font = { italic: true }
        noDataRow.alignment = { horizontal: 'center' }
        currentRow = noDataRow.number + 1
    }

    for (const group of grouped) {
        // แทรกชื่อหมอ รวมเวลางานและข้อมูลอื่นๆ (ถ้าต้องการใส่เพิ่ม)
        const docNameRow = ws.addRow([`ทันตแพทย์ : ${group.doctorFullname}`])
        ws.mergeCells(docNameRow.number, 1, docNameRow.number, ws.columns.length)
        docNameRow.font = { bold: true }
        docNameRow.alignment = { horizontal: 'left', vertical: 'middle' }
        currentRow = docNameRow.number + 1

        // เพิ่มแถวข้อมูลแต่ละรายการ
        group.rows.forEach((item: any, idx) => {
            let workingTime = '-'
            if (item.timeStart && item.timeEnd) {
                const timeStartSplit = item.timeStart.split(':')
                const timeEndSplit = item.timeEnd.split(':')
                workingTime = timeStartSplit[0] + ':' + timeStartSplit[1] + ' - ' + timeEndSplit[0] + ':' + timeEndSplit[1]
            }
            const dataRow = ws.addRow([
                idx + 1,
                moment(item.date).add(543, 'years').format('DD/MM/YYYY'), // ใช้ปีพุทธศักราชถ้าต้องการ
                item.timeStart && item.timeEnd ? workingTime : '-',
                item.appointments || 0,
                item.notYet || 0,
                item.walkIn || 0,
                (item.appointments || 0) + (item.walkIn || 0),
                numberFormat(item.total || 0),
                numberFormat(item.df || 0),
                numberFormat(item.creditCost || 0, 3, 3),
                numberFormat(item.treatmentRight || 0),
                numberFormat(item.dfNet || 0, 3, 3),
                numberFormat(item.handInsurance || 0),
                numberFormat(item.labCost || 0),
                item.dfNet > item.handInsurance ? numberFormat(item.dfNet - item.labCost || 0, 3, 3) : numberFormat(item.handInsurance - item.labCost || 0, 3, 3)
            ])
            dataRow.alignment = { vertical: 'middle' }
            dataRow.eachCell((cell, col) => {
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' },
                }
                if ([1, 2, 4, 5, 6, 7].includes(col)) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' }
                } else if ([8, 9, 10, 11, 12, 13, 14, 15].includes(col)) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' }
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' }
                }
            })
            currentRow = ws.lastRow ? ws.lastRow.number + 1 : currentRow + 1
        })

        // แถวสรุปข้อมูลรวมของหมอ
        const sumRow = ws.addRow([
            report.t('REPORT.TITLE.SUM'),
            '',
            '',
            '',
            '',
            '',
            '',
            numberFormat(_.sumBy(group.rows, 'total') || 0),
            numberFormat(_.sumBy(group.rows, 'df') || 0),
            numberFormat(_.sumBy(group.rows, 'creditCost') || 0, 3, 3),
            numberFormat(_.sumBy(group.rows, 'treatmentRight') || 0),
            numberFormat(_.sumBy(group.rows, 'dfNet') || 0, 3, 3),
            numberFormat(_.sumBy(group.rows, 'handInsurance') || 0),
            numberFormat(_.sumBy(group.rows, 'labCost') || 0),
            //numberFormat(_.sumBy(group.rows, calculateDf6) || 0, 3, 3)
            numberFormat(_.sumBy(group.rows, (o: any) => (o.dfNet > o.handInsurance ? o.dfNet - o.labCost : o.handInsurance - o.labCost)) || 0, 3, 3)
        ])
        const sumRowNumber = sumRow.number
        ws.mergeCells(sumRowNumber, 1, sumRowNumber, 7)
        sumRow.font = { bold: true }
        sumRow.alignment = { vertical: 'middle', horizontal: 'right' }
        sumRow.eachCell(cell => {
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' },
            }
        })
        currentRow = ws.lastRow ? ws.lastRow.number + 2 : currentRow + 2
    }

    // สร้างไฟล์และดาวน์โหลด
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${report.title}_${moment().format('YYYYMMDD')}.xlsx`)
}
